import React, {
  useState, createContext, useContext, useMemo, useEffect
} from 'react';

import { readString } from 'react-papaparse';

import { NEARBY_GAUGES_RADIUS, calculateHaversineDistance } from './utils';

const WLI_DATA_URL = 'https://wli.divirod.com/wli.json';

const SessionContext = createContext();

const parseHexagonsCSV = async (hexagons) => new Promise((resolve) => {
  readString(hexagons, {
    complete: ({ data }) => {
      // get dates from header row
      const dates = data[0].slice(2);

      // remove header row
      const csvRows = data.slice(1, -1);

      // get hexagon codes and gauge counts from first two columns
      const hexagonCodes = csvRows.map((row) => row[0]);
      const gaugeCounts = csvRows.map((row) => parseInt(row[1], 10));

      const wliTimeline = csvRows.map((row) => (
        row
          .slice(2) // remove hexagon and date columns
          .map((str) => (str ? parseInt(str, 10) : null)) // convert to int
      ));

      resolve([hexagonCodes, dates, wliTimeline, gaugeCounts]);
    },
  });
});

const parseGaugesCSV = async (gauges) => new Promise((resolve) => {
  readString(gauges, {
    complete: ({ data }) => {
      resolve(data.map((gauge) => {
        const [hexagonCode, lat, lng] = gauge;
        return { hexagonCode, lat: Number(lat), lng: Number(lng) };
      }));
    },
  });
});

export function SessionProvider({ children }) {
  const [dates, setDates] = useState();
  const [selectedDateIndex, setSelectedDateIndex] = useState();
  const [selectedAttribute, setSelectedAttribute] = useState('wli');
  const [hexagons, setHexagons] = useState();
  const [hexagonOpacity, setHexagonOpacity] = useState(100);
  const [gaugePositions, setGaugePositions] = useState();
  const [nearbyGauges, setNearbyGauges] = useState();
  const [selectedLocation, setSelectedLocation] = useState();
  const [selectedHexagon, setSelectedHexagon] = useState();
  const [isMobile, setIsMobile] = useState(window.innerWidth);

  // first, fetch and parse the wli/gauge data
  useEffect(() => {
    const task = async () => {
      const wliResponse = await fetch(WLI_DATA_URL);
      const wliData = await wliResponse.json();
      const { gauges, wli } = wliData;
      const [hexagonCodes, newDates, levelHistory, gaugeCounts] = await parseHexagonsCSV(wli);
      const newGaugePositions = await parseGaugesCSV(gauges);
      const maxLogGaugeCount = Math.log(gaugeCounts.reduce((a, b) => Math.max(a, b)));

      const newHexagons = hexagonCodes.map((hexagonCode, hexagonIndex) => ({
        hexagonCode,
        gaugeCount: gaugeCounts[hexagonIndex],
        gaugeCountNorm: Math.log(gaugeCounts[hexagonIndex]) / maxLogGaugeCount,
        timeline: newDates.map((_, dateIndex) => {
          const wliValue = levelHistory[hexagonIndex][dateIndex];
          if (wliValue === null) return null;
          return {
            wli: wliValue,
            wliNorm: (wliValue + 100) / 200,
          };
        })
      }));

      setDates(newDates);
      setGaugePositions(newGaugePositions);
      setSelectedDateIndex(newDates.length - 1);
      setHexagons(newHexagons);
    };
    task();
  }, []);

  // when a location (from search) finishes loading, get its nearby gauges
  useEffect(() => {
    if (!gaugePositions || !selectedLocation) {
      setNearbyGauges();
      return;
    }

    const distances = gaugePositions.map((position) => ({
      ...position,
      distance: calculateHaversineDistance(
        selectedLocation.lat,
        selectedLocation.lng,
        position.lat,
        position.lng
      )
    }));

    distances.sort((a, b) => a.distance - b.distance);

    setNearbyGauges(distances.filter(({ distance }) => distance < NEARBY_GAUGES_RADIUS));
  }, [gaugePositions, selectedLocation]);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 600);
    }

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!selectedLocation || !hexagons) {
      setSelectedHexagon();
    } else {
      setSelectedHexagon(hexagons.find((hex) => hex.hexagonCode === selectedLocation.hexagonCode));
    }
  }, [selectedLocation, hexagons]);

  const clearSelectedLocation = () => {
    setSelectedLocation();
    setNearbyGauges();
  };

  const value = useMemo(() => ({
    hexagons,
    dates,
    selectedLocation,
    setSelectedLocation,
    nearbyGauges,
    selectedAttribute,
    setSelectedAttribute,
    selectedDateIndex,
    setSelectedDateIndex,
    hexagonOpacity,
    setHexagonOpacity,
    clearSelectedLocation,
    selectedHexagon,
    isMobile
  }), [
    hexagons, dates, selectedLocation, setSelectedLocation, nearbyGauges, selectedAttribute,
    setSelectedAttribute, selectedDateIndex, setSelectedDateIndex, hexagonOpacity,
    setHexagonOpacity, clearSelectedLocation, selectedHexagon, isMobile
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionContext() {
  return useContext(SessionContext);
}
